import { useState, useEffect } from 'react';
import { trpc } from '@/data-loaders';

const FAVOURITE_COOKIE_NAME = 'favourites-v3';

interface FavouritesCookie {
  domain: string;
  favourites: string; // pipe-delimited IDs
}

// Study site sets a weirdly formed cookie value... neeed to recreate
const encodeCustom = (cookie: FavouritesCookie): string => {
  // JSON.stringify gives us {"domain":"curtin.edu.au","favourites":"MJRH-SFTENv1|B-COMPv1"}
  const json = JSON.stringify(cookie);

  // Strip outer {} and replace " and , with encoded versions
  const inner = json.slice(1, -1).replace(/"/g, '%22').replace(/,/g, '%2C');

  return `{${inner}}`;
};

const decodeCustom = (raw: string): FavouritesCookie | null => {
  try {
    const inner = raw.slice(1, -1);
    const restored = inner.replace(/%22/g, '"').replace(/%2C/g, ',');
    return JSON.parse('{' + restored + '}') as FavouritesCookie;
  } catch (e: any) {
    console.warn('Failed to decode custom cookie:', e);
    return null;
  }
};

const getCookie = (name: string): string | null => {
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(name + '='));
  return cookie ? (cookie.split('=')[1] ?? null) : null;
};

const setCookie = (name: string, value: string, domain?: string) => {
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

  let cookieStr = `${name}=${value}; path=/; expires=${oneYearFromNow.toUTCString()}; SameSite=Lax`;

  if (domain) {
    cookieStr += `; domain=${domain}`;
  }

  document.cookie = cookieStr;
};

const parseFavouritesCookie = (raw: string): FavouritesCookie | null => {
  return decodeCustom(raw);
};

const saveFavouritesCookie = (cookie: FavouritesCookie) => {
  const encoded = encodeCustom(cookie);
  setCookie(FAVOURITE_COOKIE_NAME, encoded, cookie.domain);
};

const getFavourites = (): string[] => {
  const raw = getCookie(FAVOURITE_COOKIE_NAME);
  if (!raw) return [];
  const parsed = parseFavouritesCookie(raw);
  return parsed?.favourites ? parsed.favourites.split('|') : [];
};

export const isFavourite = (id: string): boolean => {
  return getFavourites().includes(id);
};

export const toggleFavourite = (id: string): void => {
  const raw = getCookie(FAVOURITE_COOKIE_NAME);

  if (raw) {
    const parsed = parseFavouritesCookie(raw);
    if (!parsed) return;

    let favourites = parsed.favourites ? parsed.favourites.split('|') : [];

    if (favourites.includes(id)) {
      favourites = favourites.filter((fav) => fav !== id);
    } else {
      favourites.push(id);
    }

    saveFavouritesCookie({
      ...parsed,
      favourites: favourites.join('|'),
    });
    return;
  }

  // No cookie exists → create a new one
  const hostname = window.location.hostname;
  const isCurtinSubdomain = hostname === 'curtin.edu.au' || hostname.endsWith('.curtin.edu.au');

  const newCookie: FavouritesCookie = {
    domain: isCurtinSubdomain ? '.curtin.edu.au' : hostname,
    favourites: id,
  };

  saveFavouritesCookie(newCookie);
};

export const useFavourites = () => {
  const [favourites, setFavourites] = useState<string[]>([]);

  const {
    data: favouriteDetails,
    isLoading: isFavouriteDetailsLoading,
    error: favouriteDetailsError,
  } = trpc.getFavouriteCourseDetails.useQuery(
    {
      courseIds: favourites,
    },
    {
      enabled: favourites.length > 0,
      retry: false,
      placeholderData: (previousData) => {
        return previousData;
      },
    },
  );

  const emailFavouriteCourses = trpc.emailFavouriteCourses.useMutation();

  // Load favourites from cookie on mount
  useEffect(() => {
    setFavourites(getFavourites());
  }, []);

  // Poll the cookie every 1s to detect external changes
  useEffect(() => {
    const interval = setInterval(() => {
      const current = getFavourites();
      setFavourites((prev) => (JSON.stringify(prev) !== JSON.stringify(current) ? current : prev));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    emailFavouriteCourses.reset();
  }, [favourites]);

  const isFavourite = (id: string) => favourites.includes(id);

  const toggleFavourite = (id: string) => {
    // Update cookie
    const raw = getCookie(FAVOURITE_COOKIE_NAME);
    let updated: string[];

    if (raw) {
      const parsed = parseFavouritesCookie(raw);
      if (!parsed) return;

      let favs = parsed.favourites ? parsed.favourites.split('|') : [];

      if (favs.includes(id)) {
        favs = favs.filter((f) => f !== id);
      } else {
        favs.push(id);
      }

      saveFavouritesCookie({
        ...parsed,
        favourites: favs.join('|'),
      });

      updated = favs;
    } else {
      // No cookie → create new
      const hostname = window.location.hostname;
      const isCurtinSubdomain = hostname === 'curtin.edu.au' || hostname.endsWith('.curtin.edu.au');

      const newCookie = {
        domain: isCurtinSubdomain ? '.curtin.edu.au' : hostname,
        favourites: id,
      };

      saveFavouritesCookie(newCookie);
      updated = [id];
    }

    // Update React state so UI re-renders
    setFavourites(updated);
  };

  return {
    favourites,
    favouriteDetails,
    isFavouriteDetailsLoading,
    favouriteDetailsError,
    emailFavouriteCourses,
    isFavourite,
    toggleFavourite,
  };
};
